// src/utils/pricingCalculator.js
// Pure function — no side effects, no imports from Firebase or React.

/**
 * Category IDs from TAG_CATALOG that indicate a Stripe/SaaS complex session.
 * All current categories are Stripe-related and therefore map to "complex".
 */
const COMPLEX_CATEGORY_IDS = new Set([
  "stripe-webhooks-events",
  "subscriptions-billing-logic",
  "checkout-payments-access",
  "connect-payouts-reliability",
]);

/**
 * Tag keywords (lowercase) that also signal a complex Stripe/API session
 * when no category is set (e.g. problems submitted without a category).
 */
const COMPLEX_TAG_KEYWORDS = [
  "webhook",
  "subscription",
  "billing",
  "invoice",
  "paymentintent",
  "checkout",
  "sca",
  "3ds",
  "stripe",
  "connect",
  "payout",
  "dunning",
  "api",
];

const AMOUNTS = {
  emergency: 34900, // €349 in cents
  complex:   24900, // €249
  standard:  14900, // €149
};

const LABELS = {
  emergency: "Emergency Fix — €349",
  complex:   "Complex Fix — €249",
  standard:  "Standard Fix — €149",
};

const DESCRIPTIONS = {
  emergency: "Revenue blocking / production emergency",
  complex:   "Stripe, SaaS billing, webhooks, API",
  standard:  "Shopify checkout, DNS, email, site offline",
};

/**
 * Determines the session price tier from the wizard formData.
 *
 * Tier priority (highest wins):
 *   1. emergency — urgency is "critical" or "emergency"
 *   2. complex   — category is a Stripe/SaaS category OR tags contain Stripe keywords
 *   3. standard  — everything else
 *
 * @param {{ urgency?: string, categoryId?: string, selectedTags?: string[] }} formData
 * @returns {{ amount: number, label: string, tier: "standard"|"complex"|"emergency", description: string }}
 */
export function calculateSessionPrice(formData) {
  const urgency = (formData.urgency || "").toLowerCase().trim();

  // Tier 1 — Emergency
  if (urgency === "critical" || urgency === "emergency") {
    return {
      amount: AMOUNTS.emergency,
      label:  LABELS.emergency,
      tier:   "emergency",
      description: DESCRIPTIONS.emergency,
    };
  }

  // Tier 2 — Complex: Stripe category match
  const categoryId = formData.categoryId || "";
  if (COMPLEX_CATEGORY_IDS.has(categoryId)) {
    return {
      amount: AMOUNTS.complex,
      label:  LABELS.complex,
      tier:   "complex",
      description: DESCRIPTIONS.complex,
    };
  }

  // Tier 2 — Complex: tag keyword match (fallback when no category is set)
  const tagsLower = (formData.selectedTags || []).map((t) =>
    String(t).toLowerCase()
  );
  const hasComplexTag = tagsLower.some((tag) =>
    COMPLEX_TAG_KEYWORDS.some((kw) => tag.includes(kw))
  );
  if (hasComplexTag) {
    return {
      amount: AMOUNTS.complex,
      label:  LABELS.complex,
      tier:   "complex",
      description: DESCRIPTIONS.complex,
    };
  }

  // Tier 3 — Standard
  return {
    amount: AMOUNTS.standard,
    label:  LABELS.standard,
    tier:   "standard",
    description: DESCRIPTIONS.standard,
  };
}
